import { useEffect, useState } from "react";
import api, { fmtTRY, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Plus, Pencil, Trash2, Printer, X, ArrowRightCircle, CheckCircle2 } from "lucide-react";
import CustomerPicker from "../components/common/CustomerPicker";

const STATUS = {
  beklemede: { label: "Beklemede", color: "bg-amber-500 text-slate-900" },
  kabul: { label: "Kabul Edildi", color: "bg-emerald-600 text-white" },
  reddedildi: { label: "Reddedildi", color: "bg-rose-600 text-white" },
};

const TYPES = {
  sifir_traktor: { label: "Sıfır Traktör", short: "Sıfır Traktör Teklifi", color: "bg-[#1B4D3E] text-white", icon: "🚜" },
  ikinci_el_traktor: { label: "2. El Traktör", short: "2. El Traktör Teklifi", color: "bg-slate-800 text-white", icon: "🚜" },
  yedek_parca: { label: "Yedek Parça", short: "Yedek Parça Teklifi", color: "bg-[#D97706] text-white", icon: "🔧" },
  servis: { label: "Servis", short: "Servis Teklifi", color: "bg-[#EA580C] text-white", icon: "⚙️" },
};

const empty = { customer_id: "", customer_name: "", quote_type: "yedek_parca", lines: [], discount: 0, status: "beklemede", notes: "", tractor_id: "" };
const emptyLine = { description: "", quantity: 1, unit_price: 0, tax_rate: 20 };

export default function QuotationsPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [tractors, setTractors] = useState([]);
  const [parts, setParts] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [convertQ, setConvertQ] = useState(null); // the quote being converted
  const [convertForm, setConvertForm] = useState({});
  const [convertBusy, setConvertBusy] = useState(false);
  const [salespeople, setSalespeople] = useState([]);

  const load = () => api.get("/quotations").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/tractors").then((r) => setTractors(r.data)).catch(() => {});
    api.get("/parts").then((r) => setParts(r.data)).catch(() => {});
    api.get("/users").then((r) => setSalespeople((r.data || []).filter((u) => u.active !== false))).catch(() => setSalespeople([]));
  }, []);

  const openNewOfType = (t) => {
    setEditing(null);
    setForm({ ...empty, quote_type: t });
    setOpen(true);
  };

  const addLine = () => setForm({ ...form, lines: [...form.lines, { ...emptyLine }] });
  const addFromCatalog = (obj, kind) => {
    let line = { ...emptyLine };
    if (kind === "tractor") {
      line = { description: `${obj.brand} ${obj.model} ${obj.year || ""} · ${obj.engine_hp || ""}HP · ${obj.drive || ""}`.trim(), quantity: 1, unit_price: obj.sale_price, tax_rate: 20 };
      // remember tractor_id so we can convert the quote to a sale in one click
      setForm((f) => ({ ...f, tractor_id: obj.id, lines: [...f.lines, line] }));
      return;
    } else if (kind === "part") {
      line = { description: `${obj.name} ${obj.oem_no ? `(${obj.oem_no})` : ""}`.trim(), quantity: 1, unit_price: obj.sale_price, tax_rate: 20 };
    }
    setForm((f) => ({ ...f, lines: [...f.lines, line] }));
  };
  const updateLine = (i, patch) => { const l = [...form.lines]; l[i] = { ...l[i], ...patch }; setForm({ ...form, lines: l }); };
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });

  const subtotal = form.lines.reduce((s, l) => s + Number(l.quantity) * Number(l.unit_price), 0);
  const tax = form.lines.reduce((s, l) => s + Number(l.quantity) * Number(l.unit_price) * (Number(l.tax_rate) / 100), 0);
  const total = subtotal + tax - (Number(form.discount) || 0);

  const submit = async () => {
    if (!form.customer_name) return toast.error("Müşteri gerekli");
    const payload = { ...form, discount: Number(form.discount) || 0, lines: form.lines.map((l) => ({ ...l, quantity: Number(l.quantity), unit_price: Number(l.unit_price), tax_rate: Number(l.tax_rate) })) };
    try {
      if (editing) await api.put(`/quotations/${editing}`, payload);
      else await api.post("/quotations", payload);
      toast.success("Kaydedildi"); setOpen(false); setForm(empty); setEditing(null); load();
    } catch { toast.error("Kayıt başarısız"); }
  };

  const edit = (q) => { setEditing(q.id); setForm({ ...empty, ...q, lines: q.lines || [] }); setOpen(true); };
  const remove = async (id) => { if (window.confirm("Silinsin mi?")) { await api.delete(`/quotations/${id}`); toast.success("Silindi"); load(); } };

  const isConverted = (q) => !!(q.converted_sale_id || q.converted_order_id || q.converted_work_order_id);
  const isTractorType = (t) => t === "sifir_traktor" || t === "ikinci_el_traktor";

  const openConvert = (q) => {
    const base = {
      customer_id: q.customer_id || "",
      tractor_id: q.tractor_id || "",
      buyer_name: q.customer_name || "",
      buyer_phone: "",
      sale_price: q.total || 0,
      payment_method: "nakit",
      down_payment: 0,
      installments: 0,
      plate: "",
      trade_in: false,
      trade_in_desc: "",
      trade_in_value: 0,
      sale_notes: "",
      collected_amount: 0,
      collected_method: "nakit",
    };
    setConvertQ(q);
    setConvertForm(base);
  };

  const submitConvert = async () => {
    if (!convertQ) return;
    if (!convertForm.customer_id) {
      toast.error("Lütfen bir müşteri seçin veya yeni cari kart oluşturun");
      return;
    }
    if (isTractorType(convertQ.quote_type) && !convertForm.tractor_id) {
      toast.error("Satış için bir traktör seçin");
      return;
    }
    setConvertBusy(true);
    try {
      const payload = { ...convertForm };
      ["sale_price", "down_payment", "installments", "trade_in_value", "collected_amount"].forEach((k) => {
        if (payload[k] !== undefined && payload[k] !== "") payload[k] = Number(payload[k]) || 0;
      });
      const { data } = await api.post(`/quotations/${convertQ.id}/convert`, payload);
      toast.success("Satış oluşturuldu ve müşteri carisine işlendi");
      setConvertQ(null);
      setConvertForm({});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Satışa dönüştürülemedi");
    } finally {
      setConvertBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="quotations-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1B4D3E]">Satış</div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">Teklifler</h1>
      </div>

      {/* Type selector buttons */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
        {Object.entries(TYPES).map(([k, t]) => (
          <button
            key={k}
            onClick={() => openNewOfType(k)}
            className={`text-left p-4 rounded-xl border border-slate-200 bg-white hover:shadow-md transition-all hover:-translate-y-0.5 tp-fade`}
            data-testid={`new-quote-${k}`}
          >
            <div className={`inline-flex items-center gap-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${t.color}`}>
              <span>{t.icon}</span> Yeni
            </div>
            <div className="mt-2 font-extrabold text-slate-900 text-base">{t.short}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {k === "sifir_traktor" && "Bayi stoktan sıfır traktör teklifi"}
              {k === "ikinci_el_traktor" && "İkinci el stoktan traktör teklifi"}
              {k === "yedek_parca" && "Parça listesi + KDV'li fiyat"}
              {k === "servis" && "İşçilik + parça bakım paketi"}
            </div>
          </button>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {[["all", "Tümü"], ...Object.entries(TYPES).map(([k, v]) => [k, v.label])].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setFilterType(k)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${filterType === k ? "border-[#1B4D3E] text-[#1B4D3E]" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid={`filter-${k}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
        {items.filter((q) => filterType === "all" || q.quote_type === filterType).map((q) => (
          <Card key={q.id} className="border-slate-200 tp-fade" data-testid={`quote-card-${q.id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs text-slate-500 font-mono">{q.quote_no}</div>
                  <div className="font-bold text-slate-900">{q.customer_name}</div>
                  <div className="text-xs text-slate-500">{fmtDate(q.created_at)} · {q.lines?.length || 0} kalem</div>
                  {q.quote_type && <Badge className={`text-[10px] mt-1 ${TYPES[q.quote_type]?.color || "bg-slate-500 text-white"}`}>{TYPES[q.quote_type]?.label}</Badge>}
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge className={STATUS[q.status]?.color}>{STATUS[q.status]?.label}</Badge>
                  {isConverted(q) && (
                    <Badge className="bg-sky-600 text-white text-[10px] gap-1 inline-flex items-center" data-testid={`converted-badge-${q.id}`}>
                      <CheckCircle2 size={10} /> SATIŞA DÖNDÜ
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <span className="text-xs text-slate-500">Toplam</span>
                <span className="text-lg font-extrabold text-[#1B4D3E]">{fmtTRY(q.total)}</span>
              </div>
              {!isConverted(q) && q.status !== "reddedildi" && (
                <Button
                  size="sm"
                  className="w-full bg-[#D97706] hover:bg-[#B45309] text-white gap-1"
                  onClick={() => openConvert(q)}
                  data-testid={`convert-quote-${q.id}`}
                >
                  <ArrowRightCircle size={14} /> Satışı Yap
                </Button>
              )}
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="flex-1 gap-1" onClick={() => setPreview(q)} data-testid={`print-quote-${q.id}`}><Printer size={14} /> Yazdır</Button>
                <Button size="sm" variant="outline" onClick={() => edit(q)} data-testid={`edit-quote-${q.id}`}><Pencil size={14} /></Button>
                <Button size="sm" variant="outline" onClick={() => remove(q.id)} data-testid={`delete-quote-${q.id}`}><Trash2 size={14} className="text-rose-600" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <div className="col-span-full text-center py-10 text-slate-500">Teklif yok.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Teklif Düzenle" : "Yeni Teklif"} — <span className="text-[#1B4D3E]">{TYPES[form.quote_type]?.label}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Teklif Tipi</Label>
              <Select value={form.quote_type} onValueChange={(v) => setForm({ ...form, quote_type: v })}>
                <SelectTrigger data-testid="q-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(TYPES).map(([k, t]) => <SelectItem key={k} value={k}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Durum</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="q-status"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <CustomerPicker
                value={form.customer_id || ""}
                onChange={(c) => setForm((f) => ({ ...f, customer_id: c.id, customer_name: c.name }))}
                testid="q-customer-picker"
                allowManualName
                manualName={form.customer_id ? "" : form.customer_name}
                onManualName={(v) => setForm((f) => ({ ...f, customer_name: v, customer_id: "" }))}
              />
            </div>

            {/* Type-specific catalog picker */}
            <div>
              {(form.quote_type === "sifir_traktor" || form.quote_type === "ikinci_el_traktor") && (
                <>
                  <Label>Stoktan Traktör Ekle</Label>
                  <Select value="" onValueChange={(v) => { const t = tractors.find((x) => x.id === v); if (t) addFromCatalog(t, "tractor"); }}>
                    <SelectTrigger data-testid="q-add-tractor"><SelectValue placeholder="Traktör seç ve ekle" /></SelectTrigger>
                    <SelectContent>
                      {tractors.filter((t) => form.quote_type === "sifir_traktor" ? t.condition === "sifir" : t.condition === "ikinci_el").filter((t) => t.status === "stokta").map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.brand} {t.model} {t.year || ""} · {fmtTRY(t.sale_price)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
              {form.quote_type === "yedek_parca" && (
                <>
                  <Label>Stoktan Parça Ekle</Label>
                  <Select value="" onValueChange={(v) => { const p = parts.find((x) => x.id === v); if (p) addFromCatalog(p, "part"); }}>
                    <SelectTrigger data-testid="q-add-part"><SelectValue placeholder="Parça seç ve ekle" /></SelectTrigger>
                    <SelectContent>{parts.slice(0, 200).map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {fmtTRY(p.sale_price)}</SelectItem>)}</SelectContent>
                  </Select>
                </>
              )}
              {form.quote_type === "servis" && (
                <>
                  <Label>Hızlı Servis Kalemleri</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { description: "İşçilik (saat başı)", unit_price: 500 },
                      { description: "Yağ ve filtre bakımı", unit_price: 2500 },
                      { description: "Fren bakımı", unit_price: 1800 },
                      { description: "Motor arıza teşhisi", unit_price: 750 },
                    ].map((sug, i) => (
                      <Button key={i} type="button" variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, { ...emptyLine, ...sug }] }))} data-testid={`q-sugg-${i}`}>{sug.description}</Button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <Label>Kalemler</Label>
              <Button size="sm" variant="outline" onClick={addLine} className="gap-1" data-testid="q-add-line"><Plus size={14} /> Kalem Ekle</Button>
            </div>
            <div className="space-y-2">
              {form.lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center bg-slate-50 border border-slate-200 rounded-lg p-2">
                  <Input className="col-span-5" placeholder="Açıklama" value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} data-testid={`q-line-desc-${i}`} />
                  <Input className="col-span-2" type="number" placeholder="Adet" value={l.quantity} onChange={(e) => updateLine(i, { quantity: e.target.value })} data-testid={`q-line-qty-${i}`} />
                  <Input className="col-span-2" type="number" placeholder="Fiyat" value={l.unit_price} onChange={(e) => updateLine(i, { unit_price: e.target.value })} data-testid={`q-line-price-${i}`} />
                  <Input className="col-span-2" type="number" placeholder="KDV%" value={l.tax_rate} onChange={(e) => updateLine(i, { tax_rate: e.target.value })} />
                  <button className="col-span-1 text-rose-600" onClick={() => removeLine(i)}><X size={16} /></button>
                </div>
              ))}
              {form.lines.length === 0 && <div className="text-center text-sm text-slate-400 py-3">Henüz kalem eklenmedi.</div>}
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3 items-end">
            <div><Label>İskonto (₺)</Label><Input type="number" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} data-testid="q-discount" /></div>
            <div className="text-right">
              <div className="text-xs text-slate-500">Ara toplam {fmtTRY(subtotal)} · KDV {fmtTRY(tax)}</div>
              <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mt-1">Genel Toplam</div>
              <div className="text-2xl font-extrabold text-[#1B4D3E]" data-testid="q-total">{fmtTRY(total)}</div>
            </div>
          </div>

          <div className="mt-3"><Label>Not</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="q-notes" /></div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#163e32]" onClick={submit} data-testid="save-quote-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert quote → sale dialog */}
      <Dialog open={!!convertQ} onOpenChange={(v) => !v && setConvertQ(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightCircle size={18} className="text-[#D97706]" />
              Satışı Yap — <span className="text-[#1B4D3E]">{convertQ && TYPES[convertQ.quote_type]?.label}</span>
            </DialogTitle>
          </DialogHeader>
          {convertQ && (
            <div className="space-y-4">
              <div className="bg-emerald-50/70 border border-emerald-100 rounded-lg p-3 text-sm">
                <div className="font-bold text-slate-900">{convertQ.customer_name}</div>
                <div className="text-xs text-slate-500 font-mono mt-0.5">{convertQ.quote_no} · {convertQ.lines?.length || 0} kalem · Toplam {fmtTRY(convertQ.total)}</div>
              </div>

              <CustomerPicker
                value={convertForm.customer_id || ""}
                onChange={(c) => setConvertForm((f) => ({ ...f, customer_id: c.id, buyer_name: c.name, buyer_phone: c.phone || f.buyer_phone }))}
                testid="convert-customer"
                label="Müşteri Carisi"
              />

              {salespeople.length > 0 && (
                <div>
                  <Label>Satış Temsilcisi (opsiyonel)</Label>
                  <Select
                    value={convertForm.salesperson_id || "__none__"}
                    onValueChange={(v) => setConvertForm({ ...convertForm, salesperson_id: v === "__none__" ? "" : v })}
                  >
                    <SelectTrigger data-testid="convert-salesperson"><SelectValue placeholder="Satış temsilcisi seç" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Atanmasın —</SelectItem>
                      {salespeople.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.full_name || u.username}
                          {u.commission_rate > 0 && <span className="text-xs text-slate-500 ml-2">· %{u.commission_rate} komisyon</span>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {!convertQ.customer_id && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                  ⚠️ Bu teklifin ilgili carisi yok. Satışı kaydetmeden önce bir cari seçin veya <b>Yeni</b> ile hızlıca açın — tüm sipariş carisine işlenecek.
                </div>
              )}

              {isTractorType(convertQ.quote_type) && (
                <>
                  {!convertQ.tractor_id && (
                    <div>
                      <Label>Satılacak Traktör</Label>
                      <Select value={convertForm.tractor_id || ""} onValueChange={(v) => setConvertForm({ ...convertForm, tractor_id: v })}>
                        <SelectTrigger data-testid="convert-tractor"><SelectValue placeholder="Stoktaki traktörü seçin" /></SelectTrigger>
                        <SelectContent>
                          {tractors
                            .filter((t) => convertQ.quote_type === "sifir_traktor" ? t.condition === "sifir" : t.condition === "ikinci_el")
                            .filter((t) => t.status === "stokta")
                            .map((t) => <SelectItem key={t.id} value={t.id}>{t.brand} {t.model} {t.year || ""} · {fmtTRY(t.sale_price)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label>Alıcı Adı</Label><Input value={convertForm.buyer_name || ""} onChange={(e) => setConvertForm({ ...convertForm, buyer_name: e.target.value })} data-testid="convert-buyer-name" /></div>
                    <div><Label>Telefon</Label><Input value={convertForm.buyer_phone || ""} onChange={(e) => setConvertForm({ ...convertForm, buyer_phone: e.target.value })} data-testid="convert-buyer-phone" /></div>
                    <div><Label>Satış Fiyatı (₺)</Label><Input type="number" value={convertForm.sale_price || 0} onChange={(e) => setConvertForm({ ...convertForm, sale_price: e.target.value })} data-testid="convert-sale-price" /></div>
                    <div>
                      <Label>Ödeme Yöntemi</Label>
                      <Select value={convertForm.payment_method || "nakit"} onValueChange={(v) => setConvertForm({ ...convertForm, payment_method: v })}>
                        <SelectTrigger data-testid="convert-payment-method"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="nakit">Nakit</SelectItem>
                          <SelectItem value="havale">Havale/EFT</SelectItem>
                          <SelectItem value="kredi_karti">Kredi Kartı</SelectItem>
                          <SelectItem value="kredi">Banka Kredisi</SelectItem>
                          <SelectItem value="senet">Senet</SelectItem>
                          <SelectItem value="cek">Çek</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Peşinat (₺)</Label><Input type="number" value={convertForm.down_payment || 0} onChange={(e) => setConvertForm({ ...convertForm, down_payment: e.target.value })} data-testid="convert-down-payment" /></div>
                    <div><Label>Taksit Sayısı</Label><Input type="number" value={convertForm.installments || 0} onChange={(e) => setConvertForm({ ...convertForm, installments: e.target.value })} data-testid="convert-installments" /></div>
                    <div><Label>Plaka</Label><Input value={convertForm.plate || ""} onChange={(e) => setConvertForm({ ...convertForm, plate: e.target.value })} data-testid="convert-plate" /></div>
                    <div className="flex items-center gap-2 pt-6">
                      <input type="checkbox" id="trade-in" checked={!!convertForm.trade_in} onChange={(e) => setConvertForm({ ...convertForm, trade_in: e.target.checked })} data-testid="convert-trade-in" />
                      <Label htmlFor="trade-in" className="cursor-pointer">Takas Var</Label>
                    </div>
                    {convertForm.trade_in && (
                      <>
                        <div className="sm:col-span-2"><Label>Takas Açıklaması</Label><Input value={convertForm.trade_in_desc || ""} onChange={(e) => setConvertForm({ ...convertForm, trade_in_desc: e.target.value })} /></div>
                        <div><Label>Takas Değeri (₺)</Label><Input type="number" value={convertForm.trade_in_value || 0} onChange={(e) => setConvertForm({ ...convertForm, trade_in_value: e.target.value })} /></div>
                      </>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
                    💡 Peşinat ve taksit girdiyseniz, kalan tutar için otomatik <b>senet/çek</b> kayıtları açılır ve müşterinin carisine işlenir.
                  </div>
                </>
              )}

              {!isTractorType(convertQ.quote_type) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3">
                    Teklif onaylanınca <b>{convertQ.quote_type === "yedek_parca" ? "parça siparişi" : "iş emri"}</b> olarak teslim edildi şeklinde açılır ve toplam müşterinin carisine <b>borç</b> olarak işlenir. İstersen peşin tahsilatı aşağıdan girebilirsin (opsiyonel):
                  </div>
                  <div><Label>Tahsil Edilen Tutar (₺)</Label><Input type="number" value={convertForm.collected_amount || 0} onChange={(e) => setConvertForm({ ...convertForm, collected_amount: e.target.value })} data-testid="convert-collected-amount" /></div>
                  <div>
                    <Label>Tahsilat Yöntemi</Label>
                    <Select value={convertForm.collected_method || "nakit"} onValueChange={(v) => setConvertForm({ ...convertForm, collected_method: v })}>
                      <SelectTrigger data-testid="convert-collected-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nakit">Nakit</SelectItem>
                        <SelectItem value="havale">Havale/EFT</SelectItem>
                        <SelectItem value="kredi_karti">Kredi Kartı</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertQ(null)}>İptal</Button>
            <Button className="bg-[#D97706] hover:bg-[#B45309] text-white gap-2" onClick={submitConvert} disabled={convertBusy} data-testid="submit-convert">
              <ArrowRightCircle size={14} /> {convertBusy ? "Oluşturuluyor…" : "Satışı Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print preview */}
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-2xl">
          {preview && (
            <div className="print-page">
              <div className="flex items-start justify-between mb-6 border-b pb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D97706]">TraktörPro · Bayi & Servis</div>
                  <div className="text-2xl font-extrabold text-[#1B4D3E] mt-1">{TYPES[preview.quote_type]?.short?.toUpperCase() || "TEKLİF FORMU"}</div>
                  <div className="text-sm text-slate-500 font-mono mt-1">{preview.quote_no}</div>
                </div>
                <div className="text-right text-sm">
                  <div className="h-11 w-11 rounded-xl bg-[#1B4D3E] text-white grid place-items-center font-extrabold text-lg ml-auto">TP</div>
                  <div className="text-slate-500 mt-2">{fmtDate(preview.created_at)}</div>
                </div>
              </div>
              <div className="mb-4 grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Müşteri</div>
                  <div className="text-lg font-bold">{preview.customer_name}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Tip</div>
                  <div className="text-sm font-bold">{TYPES[preview.quote_type]?.label || "—"}</div>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-2">Açıklama</th>
                    <th className="text-right p-2">Adet</th>
                    <th className="text-right p-2">Br. Fiyat</th>
                    <th className="text-right p-2">KDV%</th>
                    <th className="text-right p-2">Tutar</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.lines?.map((l, i) => (
                    <tr key={i} className="border-b">
                      <td className="p-2">{l.description}</td>
                      <td className="p-2 text-right">{l.quantity}</td>
                      <td className="p-2 text-right">{fmtTRY(l.unit_price)}</td>
                      <td className="p-2 text-right">%{l.tax_rate}</td>
                      <td className="p-2 text-right font-semibold">{fmtTRY(l.quantity * l.unit_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 flex justify-end">
                <div className="w-56 space-y-1 text-sm">
                  <div className="flex justify-between"><span>Ara Toplam</span><span>{fmtTRY(preview.subtotal)}</span></div>
                  <div className="flex justify-between"><span>KDV</span><span>{fmtTRY(preview.tax)}</span></div>
                  <div className="flex justify-between"><span>İskonto</span><span>-{fmtTRY(preview.discount || 0)}</span></div>
                  <div className="flex justify-between font-extrabold text-lg text-[#1B4D3E] border-t pt-1 mt-1"><span>Toplam</span><span>{fmtTRY(preview.total)}</span></div>
                </div>
              </div>
              {preview.notes && <div className="mt-4 text-sm text-slate-600 bg-slate-50 p-3 rounded">{preview.notes}</div>}
            </div>
          )}
          <DialogFooter className="no-print">
            <Button variant="outline" onClick={() => setPreview(null)}>Kapat</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#163e32] gap-2" onClick={() => window.print()} data-testid="do-print-btn"><Printer size={16} /> Yazdır / PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
